import { throwError as observableThrowError, Observable } from 'rxjs';

import { map, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { AjaxErrorInfo } from './models/ajax-error-info';
import { LocalDataStorageService } from './local-data-storage.service';

@Injectable()
export class SmartHttpService {
  constructor(
    private http: HttpClient,
    private storage: LocalDataStorageService
  ) {}

  public get(
    url: string,
    params?: { [index: string]: any }
  ): Observable<{ success: boolean; data: any; totalCount?: number }> {
    let searchParams = new HttpParams();

    for (let p in params) {
      if (params.hasOwnProperty(p) && params[p]) {
        searchParams = searchParams.append(p, params[p]);
      }
    }

    return this.getRaw(url, null, searchParams).pipe(
      map((d) => this.extractData(d)),
      catchError((err) => {
        let processedError = this.processRawError(err);
        return observableThrowError(processedError);
      })
    );
  }

  public post(
    url: string,
    data: any
  ): Observable<{ success: boolean; data: any; totalCount?: number }> {
    let body = JSON.stringify(data);
    let headers = new HttpHeaders({ 'Content-type': 'application/json' });

    return this.postRaw(url, body, headers).pipe(
      map((d) => this.extractData(d)),
      catchError((err) => {
        let processedError = this.processRawError(err);
        return observableThrowError(processedError);
      })
    );
  }

  private processRawError(rawError: any): AjaxErrorInfo {
    let result = new AjaxErrorInfo();

    if (rawError instanceof HttpResponse) {
      let error = <HttpResponse<any>>rawError;

      result.isServerError = error.status == 500;
      result.isUnauthorized = error.status == 401;
      result.isPermissionDenied = error.status == 403;
      result.isSessionExpired = error.status == 440;

      result.errorDetails = result.isServerError ? error.body : null;
    } else {
      result.isConnectionError = true;
      result.errorDetails = rawError.toString();
    }

    return result;
  }

  private extractData(response: HttpResponse<Object>): any {
    if (this.isJson(response)) {
      return response.body as JSON;
    } else {
      return response.body.toString();
    }
  }

  private isJson(response: HttpResponse<object>) {
    let contentType = response.headers.get('content-type');
    return contentType && contentType.indexOf('application/json') !== -1;
  }

  public getRaw(
    url: string,
    headers?: HttpHeaders,
    params?: HttpParams
  ): Observable<HttpResponse<Object>> {
    headers = this.addAuthHeaders(headers);

    return this.http.get(url, {
      headers: headers,
      observe: 'response',
      params: params,
      responseType: 'json',
      withCredentials: true,
    });
  }

  public postRaw(
    url: string,
    body: any,
    headers?: HttpHeaders,
    params?: HttpParams
  ): Observable<HttpResponse<Object>> {
    headers = this.addAuthHeaders(headers);

    return this.http.post(url, body, {
      headers: headers,
      observe: 'response',
      params: params,
      responseType: 'json',
      withCredentials: true,
    });
  }

  private addAuthHeaders(headers?: HttpHeaders): HttpHeaders {
    if (headers === undefined || headers === null) {
      headers = new HttpHeaders();
    }

    if (this.storage.hasAccessToken()) {
      headers = headers.append(
        'Authorization',
        `Bearer ${this.storage.getAccessToken()}`
      );
    }

    if (this.storage.isUiLanguageSet()) {
      headers = headers.append('Accept-Language', this.storage.getUiCulture());
    }

    return headers;
  }
}
